'use client';

import { useSearchParams } from 'next/navigation';
import { products } from '@/lib/data';
import { useState, useEffect, Suspense } from 'react';
import { ShieldCheck, CreditCard, Smartphone, CheckCircle2, Lock } from 'lucide-react';
import { motion } from 'motion/react';
import Image from 'next/image';

function CheckoutContent() {
  const searchParams = useSearchParams();
  const planId = searchParams.get('plan');
  
  const [mounted, setMounted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'crypto'>('card');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    setMounted(true);
  }, []);

  // Default to first product if none selected
  const product = products.find(p => p.id === planId) || products[2]; // Default to 12m plan

  if (!mounted) return null;

  const handleCheckout = () => {
    const siteUrl = window.location.origin;
    const paymentMethodText = paymentMethod === 'card' ? 'Carte Bancaire' : 'Crypto';
    
    const message = `Nouvelle commande:
Produit: ${product.name} (${product.price}€)
Méthode de paiement: ${paymentMethodText}

Détails du client:
Prénom: ${firstName}
Nom: ${lastName}
Email: ${email}
WhatsApp: ${whatsapp}

Site: ${siteUrl}`;

    const whatsappNumber = '33745561646';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Paiement Sécurisé</h1>
        <p className="text-gray-400 flex items-center justify-center gap-2">
          <Lock className="w-4 h-4" /> Paiement Crypté SSL 256-bit
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Column: Form & Payment */}
        <div className="lg:col-span-7 space-y-8">
          {/* Billing Details */}
          <section className="bg-[#111] border border-white/10 rounded-3xl p-6 md:p-8">
            <h2 className="text-xl font-semibold text-white mb-6">Détails de facturation</h2>
            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Prénom</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" placeholder="Jean" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Nom</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" placeholder="Dupont" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Adresse Email (Pour la livraison)</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" placeholder="jean@exemple.com" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Numéro WhatsApp (Optionnel)</label>
                <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" placeholder="+33 6 12 34 56 78" />
              </div>
            </form>
          </section>

          {/* Payment Method */}
          <section className="bg-[#111] border border-white/10 rounded-3xl p-6 md:p-8">
            <h2 className="text-xl font-semibold text-white mb-6">Méthode de paiement</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-8">
              <button 
                onClick={() => setPaymentMethod('card')}
                className={`p-4 rounded-xl border flex flex-col items-center justify-center gap-3 transition-all ${paymentMethod === 'card' ? 'bg-blue-500/10 border-blue-500 text-blue-400' : 'bg-[#0a0a0a] border-white/10 text-gray-400 hover:border-white/30'}`}
              >
                <CreditCard className="w-6 h-6" />
                <span className="font-medium">Carte Bancaire</span>
              </button>
              <button 
                onClick={() => setPaymentMethod('crypto')}
                className={`p-4 rounded-xl border flex flex-col items-center justify-center gap-3 transition-all ${paymentMethod === 'crypto' ? 'bg-blue-500/10 border-blue-500 text-blue-400' : 'bg-[#0a0a0a] border-white/10 text-gray-400 hover:border-white/30'}`}
              >
                <Smartphone className="w-6 h-6" />
                <span className="font-medium">Crypto</span>
              </button>
            </div>

            {paymentMethod === 'card' ? (
              <div className="text-center py-8 animate-in fade-in slide-in-from-bottom-4">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-400">
                  Une fois que vous nous aurez envoyé un message sur WhatsApp, nous vous répondrons avec un lien de paiement sécurisé pour payer par carte.
                </p>
              </div>
            ) : (
              <div className="text-center py-8 animate-in fade-in slide-in-from-bottom-4">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-400">
                  Une fois que vous nous aurez envoyé un message sur WhatsApp, nous vous répondrons avec les instructions de paiement crypto sécurisées.
                </p>
              </div>
            )}
          </section>

          <button onClick={handleCheckout} className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] flex items-center justify-center gap-2 text-lg">
            <Lock className="w-5 h-5" /> Valider la commande - {product.price}€
          </button>
        </div>

        {/* Right Column: Order Summary */}
        <div className="lg:col-span-5">
          <div className="bg-[#111] border border-white/10 rounded-3xl p-6 md:p-8 sticky top-24">
            <h2 className="text-xl font-semibold text-white mb-6">Résumé de la commande</h2>
            
            <div className="flex gap-4 mb-6 pb-6 border-b border-white/10">
              <div className="w-20 h-20 rounded-xl overflow-hidden relative shrink-0">
                <Image src={product.image} alt={product.name} fill className="object-cover" referrerPolicy="no-referrer" />
              </div>
              <div>
                <h3 className="font-semibold text-white">{product.name}</h3>
                <p className="text-sm text-gray-400 mb-2">{product.duration}</p>
                <p className="font-bold text-white">{product.price}€</p>
              </div>
            </div>

            <div className="space-y-3 mb-6 pb-6 border-b border-white/10">
              <div className="flex justify-between text-gray-400">
                <span>Sous-total</span>
                <span>{product.price}€</span>
              </div>
              <div className="flex justify-between text-green-400">
                <span>Réduction</span>
                <span>-0.00€</span>
              </div>
              <div className="flex justify-between text-white font-bold text-xl pt-2">
                <span>Total</span>
                <span>{product.price}€</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Garantie Satisfait ou Remboursé.</strong> Si vous n'êtes pas satisfait dans les 7 jours, obtenez un remboursement complet.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Livraison Instantanée.</strong> Identifiants envoyés sur votre email immédiatement après le paiement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CheckoutPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Chargement...</div>}>
      <CheckoutContent />
    </Suspense>
  );
}
